import { privateApiClient } from './apiClient';
import { createCustomer } from './customerService';

/**
 * Service liens de paiement — aligné sur le mobile (AddPaymentLinkScreen).
 * Création : même payload que mobile ({ ...model, terminal_id, terminal: null }).
 */

/**
 * Construit le payload envoyé au backend (identique au mobile).
 * Mobile envoie : {...model, terminal_id: model?.terminal?.id, terminal: null}
 * @param {Object} input - Données du formulaire (terminal ou terminal_id, title, reason, amount, currency, company_id, method, customer, paymentMethod)
 * @returns {Object} Payload pour POST /pos/payment-links
 */
function buildPaymentLinkPayload(input) {
    const terminalId = input.terminal_id ?? input.terminal?.id ?? input.terminal ?? null;

    // Customer : même forme que le mobile (zip, phoneNumber, email, name, address, company_id, id si existant)
    const rawCustomer = input.customer ?? null;
    const customer = rawCustomer
        ? {
            id: rawCustomer.id ?? null,
            zip: rawCustomer.zip || '243',
            phoneNumber: rawCustomer.phoneNumber || '',
            email: rawCustomer.email || '',
            name: rawCustomer.name || '',
            address: rawCustomer.address || '',
            company_id: rawCustomer.company_id ?? input.company_id ?? null,
        }
        : null;

    // paymentMethod : même forme que le mobile (prefix, provider_id, suffix, account, full_name, expiry_date)
    const rawPm = input.paymentMethod ?? {};
    const paymentMethod = {
        prefix: rawPm.prefix ?? '243',
        provider_id: rawPm.provider_id ?? null,
        suffix: rawPm.suffix ?? null,
        account: rawPm.account ?? null,
        full_name: rawPm.full_name ?? null,
        expiry_date: rawPm.expiry_date ?? rawPm.expiration_date ?? null,
    };

    return {
        terminal_id: terminalId,
        terminal: null,
        title: input.title ?? '',
        reason: input.reason ?? input.message ?? input.title ?? '',
        amount: parseFloat(input.amount) || 0,
        currency: input.currency ?? 'CDF',
        company_id: input.company_id ?? null,
        method: input.method ?? 'sms',
        customer,
        paymentMethod,
    };
}

/**
 * Créer un lien de paiement et l'envoyer au client (SMS / Email / WhatsApp).
 * Même API que le mobile : POST /pos/payment-links.
 * @param {Object} paymentLinkData - Données du formulaire (comme le mobile)
 * @returns {Promise<Object>} payload du lien créé (backend retourne { error, message, payload })
 */
export const createPaymentLink = async (paymentLinkData) => {
    const payload = buildPaymentLinkPayload(paymentLinkData);

    // L'API exige un client existant (customer.id sur /pos/clients) : on crée le client saisi s'il est nouveau
    if (payload.customer && !payload.customer.id && payload.customer.name && payload.customer.phoneNumber) {
        const created = await createCustomer({ ...payload.customer, company_id: payload.company_id });
        payload.customer = { ...payload.customer, id: created?.id ?? null };
    }

    if (!payload.terminal_id) {
        throw new Error('Sélectionnez un terminal');
    }
    if (!payload.company_id) {
        throw new Error('Organisation non sélectionnée');
    }
    if (payload.amount == null || payload.amount <= 0) {
        throw new Error('Le montant est vide ou inférieur à zéro');
    }
    const method = payload.method || 'sms';
    if (method !== 'flash' && !payload.customer) {
        throw new Error('Sélectionnez le client');
    }
    if (method === 'email' && payload.customer && (!payload.customer.email || !String(payload.customer.email).trim())) {
        throw new Error('Pour un envoi par email, le client doit avoir une adresse email');
    }
    if ((method === 'sms' || method === 'whatsapp') && payload.customer && (!payload.customer.phoneNumber || !String(payload.customer.phoneNumber).trim())) {
        throw new Error('Pour un envoi par SMS ou WhatsApp, le client doit avoir un numéro de téléphone');
    }

    let data;
    try {
        ({ data } = await privateApiClient.post('/pos/payment-links', payload));
    } catch (error) {
        throw new Error(error?.response?.data?.message || error.message || 'Erreur lors de la création du lien de paiement');
    }

    if (data?.error) {
        throw new Error(data.message || 'Erreur lors de la création du lien de paiement');
    }

    return data?.payload ?? data?.contents ?? data;
};

/**
 * Récupérer la liste des liens de paiement
 * @param {Object} params - Paramètres de recherche { pge, lmt, sts, org, emt, cmr, slg }
 * @returns {Promise<Object>} { contents: Array, nextPage: number, previousPage: number }
 */
/** Première date lisible parmi les candidats (ISO, timestamp ou tableau [a, m, j, h, min, s]). */
const firstValidDate = (...values) => {
    for (const value of values) {
        if (value == null || value === '') continue;
        let d;
        if (Array.isArray(value)) {
            const [y, m = 1, day = 1, h = 0, min = 0, sec = 0] = value;
            d = new Date(y, m - 1, day, h, min, sec);
        } else {
            d = value instanceof Date ? value : new Date(value);
        }
        if (!Number.isNaN(d.getTime())) return d;
    }
    return null;
};

/** Normalise un lien API → format UI (admin + caissier). */
export const mapPaymentLinkFromApi = (link) => {
    if (!link) return null;
    const dates = link.dates || {};
    const createDate = firstValidDate(link.create_at, link.createAt, link.createdAt, dates.create_at, dates.emitted_at) || new Date();
    const expireDate = firstValidDate(link.will_expires_at, link.willExpiresAt, link.expires_at, link.expiresAt, dates.will_expires_at)
        || new Date(createDate.getTime() + 7 * 24 * 60 * 60 * 1000);
    const paidDate = firstValidDate(link.paid_at, link.paidAt, dates.paid_at);
    const status = String(link.status || 'pending').toLowerCase();
    const normalizedStatus = (status === 'cashed' || status === 'collected') ? 'collected' : status;

    return {
        id: link.id,
        code: link.code || null,
        // Référence du paiement (TXN-…-PMT…), renvoyée par le détail du lien une fois payé
        transCode: link.trans_code || link.transCode || null,
        title: link.title || 'Lien de paiement',
        amount: link.amount ?? 0,
        currency: link.currency || 'CDF',
        client: link.customer?.name || 'Client',
        clientContact: link.customer?.phoneNumber || link.customer?.email || '',
        message: link.reason || link.message || '',
        reason: link.reason || link.message || '',
        status: normalizedStatus,
        paymentMethod: link.method || null,
        method: link.method,
        createdAt: createDate,
        expiresAt: expireDate,
        paidAt: paidDate,
        link: link.link || (typeof window !== 'undefined' ? `${window.location.origin}/pay/${link.id}` : ''),
        notificationMethods: link.method === 'sms' ? ['sms'] : link.method === 'email' ? ['email'] : ['whatsapp'],
        clicks: link.clicks || 0,
        payments: normalizedStatus === 'paid' || normalizedStatus === 'cashed' ? 1 : 0,
        customer: link.customer,
        create_at: link.create_at,
        will_expires_at: link.will_expires_at,
    };
};

/** Statuts de l'API (PaymentLinkStatus) ; l'UI parle de « collecté » pour « cashed ». */
const API_PAYMENT_LINK_STATUSES = ['pending', 'paid', 'cashed', 'expired'];

export const fetchPaymentLinks = async (params = {}) => {
    const defaultParams = {
        pge: 1,
        lmt: 10,
        sts: 'all',
        emt: 'all',
        cmr: 'all',
        slg: '',
        ...params
    };
    // Un statut inconnu du serveur (ex. « collected ») provoquerait une erreur 500
    const statuses = String(defaultParams.sts || 'all')
        .split(',')
        .map((st) => (st === 'collected' ? 'cashed' : st))
        .filter((st) => API_PAYMENT_LINK_STATUSES.includes(st));
    defaultParams.sts = statuses.length ? statuses.join(',') : 'all';
    
    const { data } = await privateApiClient.get('/pos/payment-links', {
        params: defaultParams
    });
    
    return {
        contents: data?.contents || [],
        nextPage: data?.nextPage || null,
        previousPage: data?.previousPage || null
    };
};

/**
 * Récupérer les détails d'un lien de paiement
 * @param {string} paymentLinkId - ID du lien de paiement
 * @returns {Promise<Object>} Détails du lien de paiement
 */
export const fetchPaymentLinkDetails = async (paymentLinkId) => {
    const { data } = await privateApiClient.get(`/pos/payment-links/${paymentLinkId}`);
    // Backend retourne le DTO directement (ResponseEntity.ok(dto)), pas { contents }
    if (data?.error) {
        throw new Error(data.message || 'Erreur lors de la récupération du lien de paiement');
    }
    return data?.contents ?? data;
};

/**
 * Renouveler un lien de paiement
 * @param {string} paymentLinkId - ID du lien de paiement
 * @returns {Promise<Object>} Nouveau lien de paiement
 */
export const renewPaymentLink = async (paymentLinkId) => {
    const { data } = await privateApiClient.get(`/pos/payment-links/${paymentLinkId}/re-new`);
    // Backend retourne { error, message, request_id, payload }
    if (data?.error) {
        throw new Error(data.message || 'Erreur lors du renouvellement du lien de paiement');
    }
    return data?.payload ?? data?.contents ?? data;
};

/**
 * Récupérer le graphique de statut des liens de paiement d'un client
 * @param {Object} params - Paramètres { cmr, org, crc, yar }
 * @returns {Promise<Object>} Données du graphique
 */
export const fetchPaymentLinkStatusChart = async (params = {}) => {
    const { data } = await privateApiClient.get('/pos/payment-links/status-chart', {
        params
    });
    
    return data || {};
};











